<script setup lang="ts">
import { computed } from 'vue'

import { Card, FlexComponent } from '../../'
import { CriteriaConflictEnum, getCriteriaConflicts, isCriterionComplete } from '../../../adapters'
import {
  type AdvertiserGroup,
  type FilterCriterion,
  FilterCriterionCategoryEnum,
  type LoadAdvertisersForCriteria,
  type LoadClientsForCriteria,
  type LoadRevenueTiersForCriteria,
} from '../../../types'

import AddCriteriaButton from './AddCriteriaButton.vue'
import { CRITERION_DEFAULTS_BY_CATEGORY } from './criteria-defaults.helper'
import FilterCriterionRow from './FilterCriterionRow.vue'

const props = withDefaults(
  defineProps<{
    /**
     * The criteria the host offers. Anything left out is missing from the Add menu and needs no
     * loader — an advertiser-scoped host has no clients or revenue tiers of its own to filter on.
     */
    categories?: FilterCriterionCategoryEnum[]
    loadClients?: LoadClientsForCriteria
    loadRevenueTiers?: LoadRevenueTiersForCriteria
    loadAdvertisers?: LoadAdvertisersForCriteria
    hasAdvertiserSelect?: boolean
    advertiserGroup?: Pick<AdvertiserGroup, 'currency'> | null
    isDisabled?: boolean
    incompleteCriterionIds?: string[]
  }>(),
  {
    categories: () => Object.values(FilterCriterionCategoryEnum),
    loadClients: undefined,
    loadRevenueTiers: undefined,
    loadAdvertisers: undefined,
    hasAdvertiserSelect: true,
    advertiserGroup: null,
    isDisabled: false,
    incompleteCriterionIds: () => [],
  },
)

const criteria = defineModel<FilterCriterion[]>({ default: () => [] })

const TITLE = 'Filter criteria'
const DESCRIPTION = 'Define the conditions that creators must meet to be included in this segment.'
const EMPTY_TEXT = 'No filter criteria.'

const CONFLICT_MESSAGES: Record<CriteriaConflictEnum, string> = {
  [CriteriaConflictEnum.EmptyFollowerRange]: 'The follower range for this platform is empty',
  [CriteriaConflictEnum.NoCommonState]: 'No status satisfies all the conditions for this platform',
}

const INCOMPLETE_MESSAGE = 'Finish this criterion or remove it'

/**
 * Contradicting rows are flagged as soon as they contradict: they hold back the preview too, so
 * waiting for a save would leave the author with no idea why it went blank.
 */
const conflictMessageById = computed(
  () =>
    new Map(
      getCriteriaConflicts(criteria.value).flatMap(({ reason, criterionIds }) =>
        criterionIds.map((id) => [id, CONFLICT_MESSAGES[reason]] as const),
      ),
    ),
)

const getErrorMessage = (row: FilterCriterion): string | undefined =>
  conflictMessageById.value.get(row.id) ??
  (props.incompleteCriterionIds.includes(row.id) && !isCriterionComplete(row) ? INCOMPLETE_MESSAGE : undefined)

/** Locked cards have no Add button, so with no rows left there would be nothing to show at all. */
const isEmpty = computed(() => props.isDisabled && criteria.value.length === 0)

const handleRowUpdate = (index: number, row: FilterCriterion) => {
  criteria.value = criteria.value.map((item, itemIndex) => (itemIndex === index ? row : item))
}

const handleRowRemove = (index: number) => {
  criteria.value = criteria.value.filter((_, itemIndex) => itemIndex !== index)
}

const handleCategorySelect = (category: FilterCriterionCategoryEnum) => {
  const newRow = CRITERION_DEFAULTS_BY_CATEGORY[category](crypto.randomUUID())
  criteria.value = [...criteria.value, newRow]
}
</script>

<template>
  <Card :title="TITLE" :description="DESCRIPTION" :is-empty="isEmpty" :empty-text="EMPTY_TEXT">
    <FlexComponent direction="column" :gap="16">
      <FilterCriterionRow
        v-for="(row, index) in criteria"
        :key="row.id"
        :row="row"
        :load-clients="loadClients"
        :load-advertisers="loadAdvertisers"
        :has-advertiser-select="hasAdvertiserSelect"
        :load-revenue-tiers="loadRevenueTiers"
        :advertiser-group="advertiserGroup"
        :is-disabled="isDisabled"
        :error-message="getErrorMessage(row)"
        @update:row="handleRowUpdate(index, $event)"
        @remove="handleRowRemove(index)"
      />
      <AddCriteriaButton v-if="!isDisabled" :categories="categories" class="filter-criteria__add" @select="handleCategorySelect" />
    </FlexComponent>
  </Card>
</template>

<style scoped lang="scss">
.filter-criteria__add {
  align-self: flex-start;
}
</style>
